import * as React from "react";
import { useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  getAnalyticsConsent,
  initializeAcquisitionAnalytics,
  isAcquisitionAnalyticsConfigured,
  persistCampaignAttribution,
  setAnalyticsConsent,
  trackAcquisitionEventOnce,
  trackAdminPageView,
  type AnalyticsConsentValue,
} from "@shared/lib/acquisitionAnalytics";

export function AcquisitionAnalyticsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t, i18n } = useTranslation("ui");
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const search =
    (routerState.location as { searchStr?: string }).searchStr ||
    (typeof window !== "undefined" ? window.location.search : "");
  const [consent, setConsentState] = React.useState<AnalyticsConsentValue>(() =>
    getAnalyticsConsent(),
  );

  React.useEffect(() => {
    if (consent !== "granted") return;
    persistCampaignAttribution();
    initializeAcquisitionAnalytics();
    trackAdminPageView();
    if (pathname === "/signup") {
      const attribution = persistCampaignAttribution();
      trackAcquisitionEventOnce("registration_start", {
        app: "admin",
        locale: attribution.locale || i18n.language || "en",
        plan: attribution.plan,
        billing: attribution.billing,
        offer: attribution.offer,
        theme: attribution.theme,
      });
    }
  }, [consent, i18n.language, pathname, search]);

  const chooseConsent = (value: Exclude<AnalyticsConsentValue, "unknown">) => {
    setAnalyticsConsent(value);
    setConsentState(value);
  };

  return (
    <>
      {children}
      {isAcquisitionAnalyticsConfigured() && consent === "unknown" ? (
        <aside
          className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-3xl rounded-2xl border border-border bg-background/95 p-5 shadow-2xl backdrop-blur-xl md:flex md:items-center md:gap-6"
          aria-label={t("analytics.consent.message")}
          dir={document.documentElement.dir || "ltr"}
        >
          <p className="flex-1 text-sm leading-6 text-muted-foreground">
            {t("analytics.consent.message")} {" "}
            <a
              className="font-medium text-foreground underline underline-offset-4"
              href={`https://storizone.com/${i18n.language || "en"}/privacy`}
              target="_blank"
              rel="noreferrer"
            >
              {t("analytics.consent.privacy")}
            </a>
          </p>
          <div className="mt-4 flex shrink-0 gap-3 md:mt-0">
            <button
              type="button"
              className="rounded-full border border-border px-4 py-2 text-sm hover:bg-muted"
              onClick={() => chooseConsent("denied")}
            >
              {t("analytics.consent.decline")}
            </button>
            <button
              type="button"
              className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              onClick={() => chooseConsent("granted")}
            >
              {t("analytics.consent.accept")}
            </button>
          </div>
        </aside>
      ) : null}
    </>
  );
}
